import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../images/logo/logo-text.png";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../../services/AxiosInstance";
import PasswordInput from "../components/PasswordInput";
import { showMutationError } from "../../utils/showMutationError";

const FORM_STAGE_EXPIRED = "expired";
const FORM_STAGE_AWAITING_CODE = "awaitingCode";
const FORM_STAGE_SIGNED_LINK_VALID = "signedLinkValid";

const VerifyEmail = () => {
  const [formData, setFormData] = useState({
    email: "",
    token: "",
    password: "",
    password_confirmation: "",
  });

  const [formStage, setFormStage] = useState(FORM_STAGE_AWAITING_CODE);
  const [emailLocked, setEmailLocked] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const resendCooldownSeconds = 30;

  const navigate = useNavigate();
  const currentSessionUser = useSelector((state) => state.auth.authUser);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailFromLink = params.get("email");
    const verified = params.get("verified");
    const expired = params.get("expired");

    if (expired === "1") {
      setFormStage(FORM_STAGE_EXPIRED);
      if (emailFromLink) {
        setFormData((prev) => ({ ...prev, email: emailFromLink }));
      }
    } else if (emailFromLink && verified === "1") {
      setFormStage(FORM_STAGE_SIGNED_LINK_VALID);
      setFormData((prev) => ({ ...prev, email: emailFromLink }));
      setEmailLocked(true);
    } else if (emailFromLink) {
      setFormData((prev) => ({ ...prev, email: emailFromLink }));
    }

    if (window.location.search) {
      window.history.replaceState({}, "", "/verify-email");
    }
  }, []);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setInterval(() => {
      setCooldownSeconds((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post("/email/verify", data);
      return response.data;
    },
    onMutate: () => {
      const toastId = toast.loading("Verifying email...");
      return { toastId };
    },
    onSuccess: (data) => {
      toast.success(data.message);
      if (currentSessionUser && currentSessionUser.email !== formData.email) {
        navigate("/");
        return;
      }
      navigate("/login");
    },
    onError: (err) => {
      const message =
        err.response?.data?.message ||
        "Failed to verify email. Try again later.";
      toast.error(message);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.toastId) {
        toast.dismiss(context.toastId);
      }
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axiosInstance.post("/resend/email-token", payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setCooldownSeconds(resendCooldownSeconds);
      setFormStage(FORM_STAGE_AWAITING_CODE);
      setEmailLocked(true);
      setJustResent(true);
    },
    onError: (err) => {
      showMutationError(err, "We could not send a new code just now. Please try again in a moment.");
    },
  });

  const handleResendOtp = () => {
    if (!formData.email) {
      toast.error("Please enter your email address first.");
      return;
    }
    resendOtpMutation.mutate({ email: formData.email });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (mutation.isPending) return;

    if (formData.password !== formData.password_confirmation) {
      toast.error("Passwords do not match.");
      return;
    }

    mutation.mutate(formData);
  };

  const isExpiredStage = formStage === FORM_STAGE_EXPIRED;
  const showCredentialFields = !isExpiredStage;
  const showResendButton = formStage !== FORM_STAGE_SIGNED_LINK_VALID;
  const submitDisabled =
    isExpiredStage ||
    !formData.token ||
    !formData.email ||
    !formData.password ||
    !formData.password_confirmation ||
    mutation.isPending;

  return (
    <div className="authincation h-100 p-meddle">
      <div className="container h-100">
        <div className="row justify-content-center h-100 align-items-center">
          <div className="col-md-6">
            <div className="authincation-content">
              <div className="row no-gutters">
                <div className="col-xl-12">
                  <div className="auth-form">
                    <div className="text-center mb-3">
                      <Link to={"#"} className="logo">
                        <img
                          src={logo}
                          alt=""
                          className="w-100 mCS_img_loaded"
                        />
                      </Link>
                    </div>
                    <h4 className="text-center mb-4">
                      {isExpiredStage
                        ? "Your verification link has expired"
                        : "Verify your email and set a password"}
                    </h4>
                    <form onSubmit={handleSubmit}>

                      {isExpiredStage && (
                        <div className="alert alert-warning" role="alert">
                          {formData.email
                            ? "Click Resend OTP to send a fresh code to the email below. If that address is not correct, update it before resending."
                            : "Enter the email you signed up with, then click Resend OTP to get a fresh code."}
                        </div>
                      )}

                      {justResent && !isExpiredStage && (
                        <div className="alert alert-success" role="alert">
                          A new code is on its way to {formData.email}. Enter it below along with your new password.
                        </div>
                      )}

                      <div className="form-group mb-3">
                        <label>
                          <strong>Email</strong>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="hello@example.com"
                          onChange={handleInputChange}
                          id="email"
                          required
                          value={formData.email}
                          readOnly={emailLocked}
                        />
                        {emailLocked && formStage === FORM_STAGE_SIGNED_LINK_VALID && (
                          <div className="alert alert-info py-1 px-2 mt-1 mb-0 small" role="note">
                            Prefilled from your verification link.
                          </div>
                        )}
                      </div>

                      {showResendButton && (
                        <div className="form-group mb-3 d-flex justify-content-end align-items-center gap-2">
                          {cooldownSeconds > 0 && (
                            <small className="text-muted">A new code was just sent</small>
                          )}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={handleResendOtp}
                            disabled={resendOtpMutation.isPending || cooldownSeconds > 0 || !formData.email}
                          >
                            {resendOtpMutation.isPending
                              ? "Sending..."
                              : cooldownSeconds > 0
                              ? `Resend in ${cooldownSeconds}s`
                              : "Resend OTP"}
                          </button>
                        </div>
                      )}

                      {showCredentialFields && (
                        <>
                          <div className="form-group mb-3">
                            <label>
                              <strong>OTP from your email</strong>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter your one-time PIN"
                              onChange={handleInputChange}
                              id="token"
                              required
                              value={formData.token}
                            />
                          </div>

                          <div className="form-group mb-3">
                            <label>
                              <strong>New Password</strong>
                            </label>
                            <PasswordInput
                              autoComplete="new-password"
                              placeholder="Enter new password"
                              onChange={handleInputChange}
                              id="password"
                              required
                              value={formData.password}
                            />
                          </div>
                          <div className="form-group mb-3">
                            <label>
                              <strong>Confirm Password</strong>
                            </label>
                            <PasswordInput
                              autoComplete="new-password"
                              placeholder="Confirm new password"
                              onChange={handleInputChange}
                              id="password_confirmation"
                              required
                              value={formData.password_confirmation}
                            />
                          </div>

                          <div className="text-center">
                            <button
                              type="submit"
                              className="btn btn-primary btn-block"
                              disabled={submitDisabled}
                            >
                              {mutation.isPending
                                ? "Processing..."
                                : "Verify and activate account"}
                            </button>
                          </div>
                        </>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
